import React, { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";

const FILTER_LABEL_SX = {
  ...typography.bodySmall,
  color: ColorsConstants.grayFontColor,
  fontWeight: 600,
  mb: 0.5,
  display: "block",
};

/**
 * Generic read-only, searchable table for an insurance-reminder policy list
 * (Health / General / Life all share the same fetch/paginate/search shape).
 */
const PolicyTable = ({ endpoint, columns, searchPlaceholder, emptyMessage }) => {
  const { getValidToken } = useAuth();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const [searchQuery, setSearchQuery] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const params = new URLSearchParams({
        limit: controller.rowsPerPage,
        offset: controller.page * controller.rowsPerPage,
      });
      if (searchQuery.trim()) params.set("search", searchQuery.trim());

      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch policy records");
      const data = await response.json();
      setRows(data.results ?? []);
      setRowCount(data.count ?? 0);
    } catch (error) {
      console.error("Error fetching policy records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchRows, 350);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, searchQuery, endpoint]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={4}>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Search (Customer Name / Policy No.)
          </Typography>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
          />
        </Grid>
      </Grid>

      <AppTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage={emptyMessage}
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={(_, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(e) =>
          setController({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })
        }
      />
    </Box>
  );
};

export default PolicyTable;
